import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Box,
  Autocomplete,
  Typography,
} from '@mui/material';
import api from '../../api/config';
import { IMPORT_BUSINESSES } from '../../api/endpoints';
import { toast } from 'react-toastify';
import {
  buildImportBusinessFormData,
  getImportBusinessName,
  resolveImportBusinessCategoryId,
  resolveImportBusinessStateId,
  resolveImportBusinessStatusId,
  findImportBusinessOption,
} from '../../pages/importBusinesses/importBusinessApiHelpers';

const EditImportBusinessDialog = ({
  open,
  business,
  statusOptions = [],
  stateOptions = [],
  categoryOptions = [],
  onClose,
  onSaved,
}) => {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    address: '',
    status_id: '',
    state_id: '',
    business_category_id: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !business) return;
    setForm({
      name: business.name ?? '',
      phone: business.phone ?? '',
      address: business.address ?? '',
      status_id: resolveImportBusinessStatusId(business),
      state_id: resolveImportBusinessStateId(business),
      business_category_id: resolveImportBusinessCategoryId(business),
    });
  }, [open, business]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async () => {
    const id = business?.id;
    if (!id) return;
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      const formData = buildImportBusinessFormData({
        name: form.name.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        status_id: form.status_id,
        state_id: form.state_id,
        business_category_id: form.business_category_id,
      });
      const { data } = await api.post(IMPORT_BUSINESSES.BY_ID(id), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(data?.message || 'Import business updated successfully');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update data record');
    } finally {
      setSaving(false);
    }
  };

  const selectedStatus = findImportBusinessOption(
    statusOptions,
    form.status_id,
    business?.status
  );
  const selectedState = findImportBusinessOption(
    stateOptions,
    form.state_id,
    business?.state
  );
  const selectedCategory = findImportBusinessOption(
    categoryOptions,
    form.business_category_id,
    business?.business_category ?? business?.businessCategory
  );

  const optionEqual = (option, value) =>
    option != null && value != null && String(option.id) === String(value.id);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Data</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {getImportBusinessName(business)}
          </Typography>

          <TextField
            label="Name *"
            fullWidth
            value={form.name}
            onChange={handleChange('name')}
            disabled={saving}
          />
          <TextField
            label="Phone"
            fullWidth
            value={form.phone}
            onChange={handleChange('phone')}
            disabled={saving}
          />
          <TextField
            label="Address"
            fullWidth
            multiline
            rows={3}
            value={form.address}
            onChange={handleChange('address')}
            disabled={saving}
          />

          <Autocomplete
            options={statusOptions}
            getOptionLabel={(option) => option.name || ''}
            isOptionEqualToValue={optionEqual}
            value={selectedStatus}
            onChange={(_, value) =>
              setForm((prev) => ({ ...prev, status_id: value?.id ?? '' }))
            }
            disabled={saving}
            renderInput={(params) => <TextField {...params} label="Status" />}
          />

          <Autocomplete
            options={stateOptions}
            getOptionLabel={(option) => option.name || ''}
            isOptionEqualToValue={optionEqual}
            value={selectedState}
            onChange={(_, value) =>
              setForm((prev) => ({ ...prev, state_id: value?.id ?? '' }))
            }
            disabled={saving}
            renderInput={(params) => <TextField {...params} label="State" />}
          />

          <Autocomplete
            options={categoryOptions}
            getOptionLabel={(option) => option.name || ''}
            isOptionEqualToValue={optionEqual}
            value={selectedCategory}
            onChange={(_, value) =>
              setForm((prev) => ({ ...prev, business_category_id: value?.id ?? '' }))
            }
            disabled={saving}
            renderInput={(params) => <TextField {...params} label="Business Category" />}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !form.name.trim()}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditImportBusinessDialog;
